import React, { useMemo, useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useTheme } from "../../providers/ThemeContext";

type Props = {
  phone: string;
  message?: string;
  bottom?: number;
  left?: number;
  ariaLabel?: string;
  iconSize?: number;
  tooltipText?: string;
};

const WhatsAppFab: React.FC<Props> = ({
  phone,
  message = "Hi! I'm interested in your flags and printing services.",
  bottom = 24,
  left = 24,
  ariaLabel = "Chat on WhatsApp",
  iconSize = 26,
  tooltipText = "Quick response on WhatsApp!",
}) => {
  const { theme } = useTheme();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);

  // Auto-show tooltip after 1.5s, auto-hide after 5s
  useEffect(() => {
    const showTimer = setTimeout(() => setTooltipVisible(true), 1500);
    const hideTimer = setTimeout(() => {
      setTooltipVisible(false);
      setTooltipDismissed(true);
    }, 6500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

  const fabStyle: React.CSSProperties = {
    position: "fixed",
    bottom,
    insetInlineStart: left,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#25D366",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    transition: "transform .2s ease, box-shadow .2s ease",
    border: `1px solid ${theme.border}22`,
  };

  const hover = (el: HTMLAnchorElement, on: boolean) => {
    // Also show tooltip on hover if it was dismissed
    if (on && tooltipDismissed) setTooltipVisible(true);
    if (!on && tooltipDismissed) setTooltipVisible(false);

    Object.assign(
      el.style,
      on
        ? {
            transform: "translateY(-2px)",
            boxShadow:
              "0 12px 28px rgba(0,0,0,.22), 0 6px 14px rgba(0,0,0,.16)",
          }
        : { transform: "none" },
    );
  };

  const iconEl = useMemo(
    () =>
      React.createElement(
        FaWhatsapp as unknown as React.ComponentType<{ size?: number }>,
        { size: iconSize },
      ),
    [iconSize],
  );

  return (
    <>
      {/* Tooltip label */}
      <div
        style={{
          position: "fixed",
          bottom: bottom + 12,
          insetInlineStart: left + 64,
          zIndex: 9998,
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: theme.surface ?? "#1a1a1a",
          color: theme.text3,
          fontSize: 12.5,
          fontWeight: 500,
          fontFamily: "'Poppins', sans-serif",
          letterSpacing: "0.02em",
          padding: "7px 13px",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          // border: `1px solid ${theme.border ?? "rgba(255,255,255,0.08)"}`,
          whiteSpace: "nowrap",
          pointerEvents: "none",
          // Slide-in animation via opacity + transform
          opacity: tooltipVisible ? 1 : 0,
          transform: tooltipVisible ? "translateX(0)" : "translateX(-8px)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}
        aria-hidden="true"
      >
        {/* Green dot pulse */}
        <span style={{ position: "relative", display: "inline-flex" }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#25D366",
              display: "inline-block",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "#25D366",
              animation: tooltipVisible
                ? "waPulse 1.6s ease-out infinite"
                : "none",
            }}
          />
        </span>
        {tooltipText}
      </div>

      {/* FAB button */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        title="WhatsApp"
        style={fabStyle}
        onMouseEnter={(e) => hover(e.currentTarget, true)}
        onMouseLeave={(e) => hover(e.currentTarget, false)}
      >
        {iconEl}
      </a>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

        @keyframes waPulse {
          0%   { transform: scale(1);   opacity: 0.7; }
          70%  { transform: scale(2.4); opacity: 0; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default WhatsAppFab;
